import { AuthLayout } from '@/components/layouts/AuthLayout/AuthLayout';
import { Head } from '@/components/seo/Head/Head';
import { ErrorScreen } from '@/components/shared/ErrorScreen/ErrorScreen';
import { FullPageSpinner } from '@/components/shared/FullPageSpinner/FullPageSpinner';
import { LinkExpiredScreen } from '@/components/shared/LinkExpiredScreen/LinkExpiredScreen';
import { Button } from '@/components/ui/Button/Button';
import { Trans, useTranslation } from 'react-i18next';
import { useGetInviteTokenValidity } from '../../../../api/getInviteTokenValidity';

interface WelcomeStepProps {
  invitationToken: string;
  goToNextStep: () => void;
  firstName: string;
}

export const WelcomeStep = ({ invitationToken, goToNextStep, firstName }: WelcomeStepProps) => {
  const { t } = useTranslation('auth', { keyPrefix: 'register.welcomeStep' });

  const { data, isLoading, error, isError } = useGetInviteTokenValidity({
    invitationToken,
    options: {
      enabled: !!invitationToken,
    },
  });

  if (isLoading) {
    return <FullPageSpinner label={t('loading')} />;
  }

  const isTokenExpiredOnServer = !!error?.errors?.some((error) => error.code === 'INVITATION_TOKEN_EXPIRED');
  if (isTokenExpiredOnServer) {
    return <LinkExpiredScreen />;
  }

  if (isError) {
    return <ErrorScreen withSeoTitle />;
  }

  const isTokenValid = !!data?.data?.invitationValid;
  if (!isTokenValid) {
    return <LinkExpiredScreen />;
  }

  const handleContinue = () => {
    goToNextStep?.();
  };

  return (
    <AuthLayout illustration='jumping'>
      <Head title={t('seo.title')} />
      <div className='flex w-full max-w-[400px] flex-col items-center justify-center gap-8'>
        <h1 className='text-heading-xl-semibold text-center'>
          <Trans
            t={t}
            i18nKey='title'
            values={{ name: firstName }}
            components={{
              highlight: <span className='text-text-brand-primary' />,
            }}
          />
        </h1>
        <Button className='w-auto' onClick={handleContinue}>
          {t('button.label')}
        </Button>
      </div>
    </AuthLayout>
  );
};
